import {
  ErrorMessage,
  ExternalAccountConfirmation,
  getExternalAccountForVerification,
  LoadingState,
  IdentityVerificationWrapper,
  useSoftAssertFailed,
} from '@stripe-internal/connect-embedded-lib';
import React from 'react';
import type {ApolloError} from '@sail/data';
import type {MessageDescriptor} from '@sail/intl';
import {message, useIntl} from '@sail/intl';
import type {MetricTags} from '@sail/observability';
import {useMetrics} from '@sail/observability';
import {AccountManagementErrorState} from '../../Account/AccountManagement/AccountManagementErrorState';
import {messages as genericMessages} from './KeyProvider';
import useRetrieveAccountQuery from '../../Account/queries/useRetrieveAccountQuery';
import useAttemptExternalAccountChallengeMutation from './queries/useAttemptExternalAccountChallengeMutation';
import useCreateIdentityChallengeMutation from './queries/useCreateIdentityChallengeMutation';

type Props = {
  challengeId: string;
  identityAuthEligible: boolean;
  externalAccountId?: string;
  onSuccess: () => void;
  onClose: () => void;
  accountNumberVerificationEligible: boolean;
};

type Challenge = 'external_account' | 'identity';

type IdentityChallenge = {
  session: string;
  authAttemptId: string;
};

type ExternalAccounts = Parameters<typeof getExternalAccountForVerification>[0];

export const messages = {
  failedChallenge: message({
    id: 'connectEmbed.externalAccountConfirm.failedChallenge',
    description:
      'Error message when the user does not correctly input their existing bank account or debit card information',
    defaultMessage:
      'The information you have entered does not match the information we have on file. Please try again.',
  }),
  invalidChallenge: message({
    id: 'connectEmbed.externalAccountConfirm.invalidChallenge',
    description:
      'Error message shown when something unexpected happens when attempting to verify the external account',
    defaultMessage: 'Something unexpected happened. Please try again.',
  }),
  identityChallengeError: message({
    id: 'connectEmbed.externalAccountConfirm.identityChallengeError',
    description:
      'Error message shown when something unexpected happens when attempting to verify the external account',
    defaultMessage:
      'We cannot perform the identity verification required to update your bank account at this time.  Please try again later or contact support.',
  }),
};

const getChallengeVerificationErrorMessage = (
  error: ApolloError,
): MessageDescriptor<never> => {
  if (
    error.graphQLErrors.find(
      (gqlError: any) => gqlError.code === 'failed_challenge',
    )
  ) {
    return messages.failedChallenge;
  } else {
    return messages.invalidChallenge;
  }
};

export const ExternalAccountConfirmationWrapper = ({
  challengeId,
  identityAuthEligible,
  accountNumberVerificationEligible,
  externalAccountId,
  onSuccess,
  onClose,
}: Props) => {
  const intl = useIntl();
  const metrics = useMetrics();
  const [identityChallenge, setIdentityChallenge] =
    React.useState<IdentityChallenge>();
  const [challenge, setChallenge] =
    React.useState<Challenge>('external_account');
  const {
    data: accountData,
    loading: retrieveLoading,
    error: retrieveError,
    refetch: refetchAccount,
  } = useRetrieveAccountQuery();

  const [attemptChallenge, {error: attemptError, loading: attemptLoading}] =
    useAttemptExternalAccountChallengeMutation();

  const [
    createIdentityChallenge,
    {loading: identityChallengeLoading, error: identityChallengeError},
  ] = useCreateIdentityChallengeMutation();

  const softAssertFailed = useSoftAssertFailed();

  const onSuccessWrapper = React.useCallback(
    (type: 'identity' | 'dashboard_form') => {
      const tags: MetricTags<'connect.auth.external_account_verification.verification.successful'> =
        {challenge_type: type};

      metrics.increment(
        'connect.auth.external_account_verification.verification.successful',
        tags,
      );

      return onSuccess;
    },
    [onSuccess, metrics],
  );

  const handleSubmitChallenge = React.useCallback(
    async (token: string) => {
      // Despite the param name, v1ConnectExternalAccountChallengeChallengeToken can take a token or a raw account number as the tokenized_external_account.
      // Tokenization was removed from bank account verification intentionally because it fails for deny-listed bank accounts
      // bank accounts are deny-listed automatically on failed payout, so it's important we don't make changing the bank account after failed payout impossible
      // https://confluence.corp.stripe.com/display/T2RC/Blocklisted+Bank+Accounts+-+ACH+Debit+Declines+and+Failed+Payouts
      // Tokenization of bank accounts is unnecessary for stripe-surfaces, since we own this frontend surface and are not storing this data on the client
      await attemptChallenge({
        variables: {
          input: {
            challenge_token: challengeId,
            tokenized_external_account: token,
          },
        },
        onCompleted: (data: any) => {
          if (data.v1ConnectExternalAccountChallengeChallengeToken?.key) {
            onSuccessWrapper('dashboard_form')();
          }
        },
      }).catch(() => {
        // Because of https://github.com/apollographql/apollo-client/issues/9669, we need to catch the calls to queries
        // otherwise they fail in tests. We can remove this once we upgrade @apollo/client
      });
    },
    [attemptChallenge, onSuccessWrapper, challengeId],
  );

  const handleIdentityChallenge = React.useCallback(() => {
    setIdentityChallenge(undefined);

    createIdentityChallenge({
      variables: {
        input: {
          source: 'external_account_update',
        },
      },
      onCompleted: (data: any) => {
        setIdentityChallenge({
          session: data.v1ConnectIdentityChallenge.verification_session,
          authAttemptId: data.v1ConnectIdentityChallenge.auth_attempt_id,
        });
        setChallenge('identity');
      },
    }).catch((_e) => {
      // Because of https://github.com/apollographql/apollo-client/issues/9669, we need to catch the calls to queries
      // otherwise they fail in tests. We can remove this once we upgrade @apollo/client
    });
  }, [createIdentityChallenge]);

  const externalAccount = accountData?.v1Account?.external_accounts?.data
    ? getExternalAccountForVerification(
        // Forced casting introduced during the migration to sdk/data.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        accountData.v1Account.external_accounts.data as ExternalAccounts,
        softAssertFailed,
        accountData.v1Account.default_currency,
        externalAccountId,
      )
    : null;
  const error =
    accountData?.v1Account?.external_accounts?.data &&
    accountData.v1Account.external_accounts.data.length === 0
      ? new Error('No external accounts found.')
      : null;
  const cardExpired = externalAccount
    ? externalAccount?.object === 'card' &&
      new Date() >=
        // Months are 0-indexed in the Javascript Date API
        new Date(externalAccount.exp_year, externalAccount.exp_month - 1)
    : false;

  const isFinancialAccountDefault = !accountNumberVerificationEligible;

  // Switch to identity verification automatically if card is expired or a financial account is the default external account
  React.useEffect(() => {
    if (
      challenge !== 'identity' &&
      identityAuthEligible &&
      (cardExpired || isFinancialAccountDefault)
    ) {
      setChallenge('identity');
      handleIdentityChallenge();
    }
  }, [
    challenge,
    cardExpired,
    identityAuthEligible,
    handleIdentityChallenge,
    isFinancialAccountDefault,
  ]);

  if (retrieveLoading || attemptLoading || identityChallengeLoading) {
    return <LoadingState />;
  } else if (retrieveError || !accountData) {
    return <AccountManagementErrorState refetchAccount={refetchAccount} />;
  } else if (identityChallengeError) {
    return (
      <ErrorMessage>
        {intl.formatMessage(messages.identityChallengeError)}
      </ErrorMessage>
    );
  } else if (error) {
    return (
      <ErrorMessage>
        {intl.formatMessage(genericMessages.genericError)}
      </ErrorMessage>
    );
  } else if (identityChallenge && challenge === 'identity') {
    return (
      <IdentityVerificationWrapper
        session={identityChallenge.session}
        authAttemptId={identityChallenge.authAttemptId}
        onSuccess={onSuccessWrapper('identity')}
        retryVerification={handleIdentityChallenge}
        overrideBranding
      />
    );
  } else if (externalAccount && challenge === 'external_account') {
    return (
      <ExternalAccountConfirmation
        accountType={accountData.v1Account?.type}
        externalAccount={externalAccount}
        isEligibleForIdentityVerification={identityAuthEligible}
        onVerifyIdentity={handleIdentityChallenge}
        onSubmitCode={handleSubmitChallenge}
        onClose={onClose}
        sending={attemptLoading}
        error={
          attemptError &&
          new Error(
            intl.formatMessage(
              getChallengeVerificationErrorMessage(attemptError),
            ),
          )
        }
      />
    );
  } else {
    return null;
  }
};
